/**
 * Core layout primitives and the interactive press target the design
 * system's buttons are built on.
 *
 * Most exports are thin `<View>` wrappers that show up in almost every
 * component tree.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import React, { useCallback, useState } from 'react';
import type { LayoutChangeEvent, Role, StyleProp, ViewProps, ViewStyle } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { makeTestID } from 'util/stdtypes';

import { HeadWhenOnPage } from './navigation/head-when-on-page';
import { useTranslation } from './translation';

const MIN_HEIGHT_CLICKZONE = 44;
const MIN_WIDTH_CLICKZONE = 44;

export type PadProps = ViewProps & {
    size?: number;
};

export function Pad({ size = 20, ...rest }: PadProps) {
    return <View style={{ height: size, width: size }} {...rest} />;
}

/** @public */
export type HoverViewProps = {
    ariaLabel?: TLabel;
    ariaHidden?: boolean;
    ariaText?: string;
    role?: Role;
    hoverStyle?: StyleProp<ViewStyle>;
    pressedStyle?: StyleProp<ViewStyle>;
    shrink?: boolean;
    stopPropagation?: boolean;
    tabIndex?: 0 | -1;
    setHover?: (h: boolean) => void;
    setPressed?: (p: boolean) => void;
    /**
     * WARN: this property is being used in `notification-toast.tsx` but it is
     * not possible to set it on the `Pressable` component this should be fixed!
     */
    icon?: React.ComponentType<any>;
} & React.ComponentPropsWithoutRef<typeof Pressable>; // FIXME: Should be PressableProps

function useHandleHoverDisabled(
    disabled: boolean | null | undefined,
    localHover: boolean,
    setLocalHover: (h: boolean) => void,
    setHover: ((h: boolean) => void) | undefined,
) {
    React.useEffect(() => {
        if (disabled && localHover) {
            setLocalHover(false);
            setHover?.(false);
        }
    }, [disabled, localHover, setHover, setLocalHover]);
}

/**
 * The fundamental interactive primitive: wraps `Pressable`, tracks
 * hover/press state, and forwards `hoverStyle`/`pressedStyle`.
 *
 * Degrades to a plain `<View>` when `onPress` is absent, so no spurious
 * accessibility role gets emitted, and wraps its children in
 * `<AccessiblePointerTarget>` to guarantee a 44×44 pt tap target. Hover
 * events can be exercised in demos via the HOVER action.
 */
export const HoverView = React.forwardRef<View, HoverViewProps>(function HoverView(
    {
        style,
        ariaHidden,
        ariaLabel,
        ariaText,
        testID,
        role,
        hoverStyle,
        pressedStyle,
        children,
        disabled,
        onPress,
        shrink,
        stopPropagation,
        setHover,
        setPressed,
        ...props
    }: HoverViewProps,
    ref,
) {
    const [localHover, setLocalHover] = useState(false);
    const [localPressed, setLocalPressed] = useState(false);
    const tAriaLabel = useTranslation(ariaLabel);

    useHandleHoverDisabled(disabled, localHover, setLocalHover, setHover);

    const computedStyle = React.useMemo(
        () => (typeof style === 'function' ? style({ hovered: localHover, pressed: localPressed }) : style),
        [style, localHover, localPressed],
    );

    const computedChildren = React.useMemo(
        () => (typeof children === 'function' ? children({ hovered: localHover, pressed: localPressed }) : children),
        [children, localHover, localPressed],
    );

    if (!onPress) {
        return (
            <View style={[computedStyle, shrink ? { flexShrink: 1 } : null]} testID={makeTestID({ testID, ariaLabel })}>
                {computedChildren}
            </View>
        );
    }

    return (
        <Pressable
            aria-disabled={!!disabled}
            aria-hidden={ariaHidden}
            aria-label={tAriaLabel ?? ariaText}
            disabled={!!disabled}
            onHoverIn={() => {
                setLocalHover(true);
                setHover?.(true);
            }}
            onHoverOut={() => {
                setLocalHover(false);
                setHover?.(false);
            }}
            onPress={
                stopPropagation
                    ? (e) => {
                          e.stopPropagation();
                          onPress?.(e);
                      }
                    : onPress
            }
            onPressIn={() => {
                setLocalPressed(true);
                setPressed?.(true);
            }}
            onPressOut={() => {
                setLocalPressed(false);
                setPressed?.(false);
            }}
            ref={ref}
            role={role}
            style={[computedStyle, localHover ? hoverStyle : null, localPressed ? pressedStyle : null, shrink ? { flexShrink: 1 } : null]}
            testID={makeTestID({ testID, ariaLabel })}
            {...props}
        >
            <AccessiblePointerTarget>{computedChildren}</AccessiblePointerTarget>
        </Pressable>
    );
});

function InvisibleExtendedClickArea() {
    const s = InvisibleExtendedClickAreaStyle;
    return <View aria-hidden style={s.clickArea} />;
}

// HACK: React-native doesn't support non-numeric positions.
const InvisibleExtendedClickAreaStyle = StyleSheet.create({
    clickArea: {
        position: 'absolute',
        left: '50%' as any,
        top: '50%' as any,
        width: MIN_WIDTH_CLICKZONE,
        height: MIN_HEIGHT_CLICKZONE,
        transform: 'translate(-50%, -50%)' as any,
        minWidth: '100%' as any,
        minHeight: '100%' as any,
    },
});

export type CardProps = { testID?: string; children: React.ReactNode };

export function Card({ testID, children }: CardProps) {
    const s = useCardStyles();
    return (
        <View style={s.card} testID={testID}>
            {children}
        </View>
    );
}

const useCardStyles = createThemedStyles((tokens) => ({
    card: {
        paddingBottom: 20,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderBottomColor: tokens.colors.colorBorderPrimary,
        borderBottomWidth: 1,
    },
}));

export type PadBoxProps = {
    children: React.ReactNode;
    horiz?: number;
    vert?: number;
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
};

export function PadBox({ children, horiz, vert, top, bottom, left, right }: PadBoxProps) {
    return (
        <View
            style={{
                paddingHorizontal: horiz,
                paddingVertical: vert,
                paddingTop: top,
                paddingBottom: bottom,
                paddingLeft: left,
                paddingRight: right,
            }}
        >
            {children}
        </View>
    );
}

export type HorizBoxProps = {
    children: React.ReactNode;
    gap?: number;
    center?: boolean;
    shrink?: boolean;
    right?: boolean;
    spread?: boolean;
    wide?: boolean;
    role?: Role;
    testID?: string;
    wrap?: boolean;
};

export function HorizBox({ children, gap, center, shrink, right, spread, wide, role, testID, wrap }: HorizBoxProps) {
    return (
        <View
            role={role}
            style={{
                flexDirection: 'row',
                alignItems: center ? 'center' : 'flex-start',
                justifyContent: spread ? 'space-between' : right ? 'flex-end' : 'flex-start',
                flexShrink: shrink ? 1 : 0,
                width: wide ? '100%' : undefined,
                gap,
                flexWrap: wrap ? 'wrap' : 'nowrap',
            }}
            testID={testID}
        >
            {children}
        </View>
    );
}

export type VertBoxProps = {
    children: React.ReactNode;
    gap?: number;
    wide?: boolean;
    grow?: boolean;
    center?: boolean;
    role?: Role;
    testID?: string;
    ariaLabel?: string;
};

export function VertBox({ children, gap, wide = false, grow = false, center = false, role, testID, ariaLabel }: VertBoxProps) {
    return (
        <View
            aria-label={ariaLabel}
            role={role}
            style={{
                flexDirection: 'column',
                alignItems: center ? 'center' : wide ? undefined : 'flex-start',
                width: wide ? '100%' : null,
                flexGrow: grow ? 1 : undefined,
                gap,
            }}
            testID={testID}
        >
            {children}
        </View>
    );
}

export type NarrowProps = { children: React.ReactNode; pad?: boolean };

export function Narrow({ children, pad = true }: NarrowProps) {
    return (
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', paddingVertical: pad ? 20 : undefined }}>
            <View style={{ maxWidth: 600, flexShrink: 1, flexGrow: 1, marginHorizontal: pad ? 20 : undefined }}>{children}</View>
        </View>
    );
}

export type CenterProps = { children: React.ReactNode; pad?: number };

export function Center({ children, pad = 0 }: CenterProps) {
    return <View style={{ alignSelf: 'center', alignItems: 'center', margin: pad }}>{children}</View>;
}

export type AdaptiveWidthProps = {
    children: React.ReactNode;
    setNarrow: (narrow: boolean) => void;
    breakpoint?: number;
    style?: StyleProp<ViewStyle>;
};

export function AdaptiveWidth({ children, setNarrow, breakpoint = 540, style }: AdaptiveWidthProps) {
    const handleLayout = useCallback(
        (event: any) => {
            const { width } = event.nativeEvent.layout;
            setNarrow(width < breakpoint);
        },
        [setNarrow, breakpoint],
    );

    if (React.Children.count(children) === 1 && React.isValidElement(children)) {
        const child = React.Children.only(children) as React.ReactElement;
        return React.cloneElement(child, {
            // @ts-ignore on upgrade to React 19 (FIXME)
            onLayout: handleLayout,
            // @ts-ignore on upgrade to React 19 (FIXME)
            style: [child.props.style, style],
        });
    }
    return (
        <View onLayout={handleLayout} style={style}>
            {children}
        </View>
    );
}
export type HeaderBoxProps = {
    children: React.ReactNode;
    backgroundColor?: string;
    horiz?: number;
    vert?: number;
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
};

export function HeaderBox({ children, backgroundColor, horiz = 20, vert = 20, top, bottom, left, right }: HeaderBoxProps) {
    const { colors } = useTheme();
    return (
        <View
            style={{
                paddingHorizontal: horiz,
                paddingVertical: vert,
                paddingTop: top,
                paddingBottom: bottom,
                paddingLeft: left,
                paddingRight: right,
                backgroundColor: backgroundColor ?? colors.colorSurfaceHighlight,
            }}
        >
            {children}
        </View>
    );
}

export type SeparatedBoxProps = { children: React.ReactNode; gap?: number; separator?: typeof Separator };

// Note: Unlike the regular `gap` property, this component can't tell whether a
// component renders to `null` and so still see separators between children that
// render to null.
export function SeparatedBox({ children, gap = 0, separator = Separator }: SeparatedBoxProps) {
    const childrenArray = React.Children.toArray(children);

    return (
        <View style={{ flexDirection: 'column', gap }}>
            {childrenArray.map((child, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                    {child}
                    {index < childrenArray.length - 1 && React.createElement(separator)}
                </React.Fragment>
            ))}
        </View>
    );
}

export function Separator() {
    const s = useSeparatorStyles();
    return <View style={s.separator} />;
}

const useSeparatorStyles = createThemedStyles((tokens) => ({
    separator: {
        height: 1,
        backgroundColor: tokens.colors.colorBorderPrimary,
    },
}));

export function WindowTitle({ label, text, formatParams }: { label?: TLabel; text?: string; formatParams?: FormatParams }) {
    const tLabel = useTranslation(label, formatParams);
    return (
        <HeadWhenOnPage>
            <title>{tLabel ?? text ?? ''}</title>
        </HeadWhenOnPage>
    );
}

// Matches React Aria's VisuallyHidden property set: removes content from the
// visual layout while keeping it in the accessibility tree so screen readers
// still announce it. A plain object rather than StyleSheet.create because
// clip/whiteSpace are web-only CSS properties not in RN's ViewStyle.
const visuallyHiddenStyle = {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden' as const,
    clip: 'rect(0, 0, 0, 0)' as any,
    whiteSpace: 'nowrap' as any,
    borderWidth: 0,
};

// Content hidden from view but available to assistive technology. Pass
// aria-live/role through for live-region announcements.
export function VisuallyHidden({ children, ...rest }: ViewProps) {
    return (
        <View {...rest} style={visuallyHiddenStyle}>
            {children}
        </View>
    );
}

export type HoverSelectBoxProps = { selected: boolean; testID: string; onPress: () => void; children: React.ReactNode };

// HACK: Bump the key to force a re-render, otherwise the old hover state is
// preserved when the component is unselected, and potentially moves to longer
// be under the mouse without getting a hover out event
export function HoverSelectBox({ selected, testID, onPress, children }: HoverSelectBoxProps) {
    const s = useHoverSelectBoxStyles();
    const [key, setKey] = useState(0);
    function onLocalPress() {
        onPress();
        setKey(key + 1);
    }
    return (
        <HoverView disabled={!onPress || selected} hoverStyle={s.hover} key={key} onPress={onLocalPress} style={s.outline} testID={testID}>
            {children}
        </HoverView>
    );
}
const useHoverSelectBoxStyles = createThemedStyles((tokens) => ({
    hover: {
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
    },
    outline: {
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
    },
}));

export type ShadowBoxProps = { children: React.ReactNode };

export function ShadowBox({ children }: ShadowBoxProps) {
    const s = ShadowBoxStyle;
    return <View style={s.shadow}>{children}</View>;
}

const ShadowBoxStyle = StyleSheet.create({
    shadow: {
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        borderRadius: 12,
    },
});

export type FlowBoxProps = {
    children: React.ReactNode;
    center?: boolean;
    rowGap?: number;
    columnGap?: number;
    style?: StyleProp<ViewStyle>;
};

export function FlowBox({ children, center = false, rowGap, columnGap, style }: FlowBoxProps) {
    return (
        <View
            style={[
                {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: center ? 'center' : 'flex-start',
                    rowGap,
                    columnGap,
                },
                style,
            ]}
        >
            {children}
        </View>
    );
}

export type ContainerProps = { children: React.ReactNode };

export function Container({ children }: ContainerProps) {
    const s = useContainerStyles();
    return <View style={s.outer}>{children}</View>;
}

const useContainerStyles = createThemedStyles((tokens) => ({
    outer: {
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 12,
    },
}));

/**
 * Guarantees a 44×44 pt tap target: measures its children, and if either
 * dimension comes in under that, adds an invisible overlay
 * (`InvisibleExtendedClickArea`) sized to cover the gap.
 */
export function AccessiblePointerTarget({ children }: { children: React.ReactNode }) {
    const [needsExtendedClickArea, setNeedsExtendedClickArea] = useState(false);

    const handleLayout = useCallback((event: LayoutChangeEvent) => {
        const { height, width } = event.nativeEvent.layout || {};
        if (height <= MIN_HEIGHT_CLICKZONE || width <= MIN_WIDTH_CLICKZONE) {
            setNeedsExtendedClickArea(true);
        }
    }, []);

    const extendedClickArea = needsExtendedClickArea ? <InvisibleExtendedClickArea /> : null;

    return (
        <>
            {children}
            <View
                onLayout={handleLayout}
                style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            />
            {extendedClickArea}
        </>
    );
}
